/**
 * useSoloLeaderboard — the per-time-control Solo Puzzle Royale leaderboard.
 *
 * Mirrors the dashboard's useLeaderboardData shape (react-query, keepPreviousData,
 * short refetchInterval) but hits the solo endpoint and is scoped to a time
 * control. In-game, `timeControlSec` is seeded from the live session's timerSec so
 * the player sees their own board immediately (see SoloLeaderboardPanel).
 *
 * The Django endpoint returns the data object DIRECTLY (not a {success,data}
 * envelope), per fun_challenges/solo_leaderboard.py.
 *
 * Freshness model (NO background poll): a best-score board barely churns, so a
 * 30s timer was ~120 wasted calls/hr to catch an event that rarely happens while
 * you're looking. Instead:
 *   1. fetch on mount (react-query default),
 *   2. invalidate on YOUR game end (SL-7 — your row updates instantly), and
 *   3. refetch-on-window-focus, ENABLED ONLY AFTER you've finished a game this
 *      session (`focusRefetchEnabled`) — so once you've played, tab-away/return
 *      catches up on others' movements at zero idle cost. Pre-game the board is
 *      just the mount snapshot (acceptable — a returning pre-game viewer is rare
 *      and the board is sticky). The server also busts its Redis key on write.
 */

import {useMemo} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';

import ChessRestClient from '@/Api/ChessRestClient';

export type SoloDuration = 'daily' | 'weekly' | 'alltime';
export type SoloMetric = 'points' | 'score';

export interface SoloLeaderboardEntry {
  rank: number;
  userid: number;
  name: string;
  avatarUrl?: string;
  value: number;
  you: boolean;
}

export interface SoloLeaderboardMe {
  userid: number;
  rank: number | null;
  value: number;
  name: string;
  avatarUrl?: string;
  inTop: boolean;
}

export interface SoloLeaderboardSnapshot {
  duration: SoloDuration;
  metric: SoloMetric;
  timeControlSec: number;
  totalPlayers: number;
  leaderValue: number;
  entries: SoloLeaderboardEntry[];
  me: SoloLeaderboardMe | null;
}

interface ApiEntry {
  rank: number;
  userid: number;
  name: string;
  avatar_url?: string;
  value: number;
}
interface ApiData {
  period: SoloDuration;
  metric: SoloMetric;
  time_control_sec: number;
  total_players: number;
  leader_value: number;
  entries: ApiEntry[];
  me?: {
    userid: number;
    rank: number | null;
    value: number;
    name: string;
    avatar_url?: string;
    in_top: boolean;
  };
}

const TOP_N = 5;

async function fetchSoloLeaderboard(
  duration: SoloDuration,
  metric: SoloMetric,
  timeControlSec: number,
): Promise<SoloLeaderboardSnapshot> {
  const res = await ChessRestClient.post(
    '/fun_challenges/v1/solo_royale_leaderboard/',
    {
      duration,
      metric,
      time_control_sec: timeControlSec,
      top_n: TOP_N,
    },
  );
  const d = res.data as ApiData;
  const myId = d.me?.userid;
  return {
    duration: d.period,
    metric: d.metric,
    timeControlSec: d.time_control_sec,
    totalPlayers: d.total_players ?? 0,
    leaderValue: d.leader_value ?? 0,
    entries: (d.entries || []).map(e => ({
      rank: e.rank,
      userid: e.userid,
      name: e.name,
      avatarUrl: e.avatar_url || undefined,
      value: e.value,
      you: e.userid === myId,
    })),
    me: d.me
      ? {
          userid: d.me.userid,
          rank: d.me.rank ?? null,
          value: d.me.value ?? 0,
          name: d.me.name ?? '',
          avatarUrl: d.me.avatar_url || undefined,
          inTop: !!d.me.in_top,
        }
      : null,
  };
}

/** react-query key for the solo board — exported so SL-7 can invalidate it. */
export const soloLeaderboardKey = (
  duration: SoloDuration,
  metric: SoloMetric,
  timeControlSec: number,
) => ['soloLeaderboard', duration, metric, timeControlSec] as const;

export default function useSoloLeaderboard(
  duration: SoloDuration,
  metric: SoloMetric,
  timeControlSec: number,
  /**
   * Turn on refetch-on-window-focus. The caller flips this true once the player
   * has finished a game this session, so a tab-away/return then catches up on
   * other players' scores. No background poll — see the file header.
   */
  focusRefetchEnabled: boolean = false,
) {
  const query = useQuery<SoloLeaderboardSnapshot, Error>(
    soloLeaderboardKey(duration, metric, timeControlSec),
    () => fetchSoloLeaderboard(duration, metric, timeControlSec),
    {
      enabled: Number.isFinite(timeControlSec) && timeControlSec > 0,
      // No refetchInterval — freshness is mount + invalidate-on-end + (post-game)
      // focus refetch. staleTime 0 so a focus refetch after the game actually
      // hits the network instead of serving a "fresh" cache.
      keepPreviousData: true,
      staleTime: 0,
      refetchOnWindowFocus: focusRefetchEnabled,
    },
  );

  const snapshot = query.data ?? null;

  return useMemo(
    () => ({
      snapshot,
      isLoading: query.isLoading,
      isError: query.isError,
      refetch: query.refetch,
    }),
    [snapshot, query.isLoading, query.isError, query.refetch],
  );
}

/**
 * Helper for SL-7: invalidate every solo-leaderboard query (all durations/metrics
 * for the given time control, or all if omitted) so the board refetches with a
 * just-saved score. Call from the results-POST onSuccess.
 */
export function useInvalidateSoloLeaderboard() {
  const qc = useQueryClient();
  return (timeControlSec?: number) =>
    qc.invalidateQueries({
      predicate: q => {
        const k = q.queryKey as unknown[];
        if (k[0] !== 'soloLeaderboard') {
          return false;
        }
        return timeControlSec == null || k[3] === timeControlSec;
      },
    });
}
